import nativeBridge from '@ifit/native-webview-interface';
import { goBack } from 'connected-react-router';
import { resetEntryPoint, createSearchInitiatedFrom } from '../actions/config';
import {
  getCurrentLocation,
  getCurrentPoint,
  getChallengesEntry,
  getSearchInitiatedFrom,
} from '../selectors';
import {
  areFilterOptionsShowing,
  hideFilterOptions,
} from '../../helpers/filter-options';
import { navigateTo } from '../actions/navigateTo';
import { parsePath, parseBasePath } from '../../helpers/parsers';
import { getSearchState } from '../../views/search/state/search-state';
import { libraryPageTitle } from '../../i18n/translations';
import { setToolbarTitle } from '../../store/to-native';
import { getItem, setItem } from '../../helpers/local-storage';

interface IStateLocationDetails {
  isChallengesEntry: boolean;
  isTrophyCaseEntry: boolean;
  searchInitiatedFrom: string;
  currentPoint: string;
  entryPoint: string;
  currentPage: string;
}

const handlerName = 'BackClicked';
const lsKey = 'trophy-case-entry';

function hideFilterMenu(): void {
  if (areFilterOptionsShowing()) {
    hideFilterOptions();
    if (getSearchState().executedASearch) {
      // We closed the filters, and will now show them their results again.
      return;
    }
  }
}

function extractStateLocationDetails(state: any): IStateLocationDetails {
  const { pathname: currentPage } = getCurrentLocation(state);
  const isTrophyCaseEntry = getItem(lsKey);
  return {
    isChallengesEntry: getChallengesEntry(state),
    isTrophyCaseEntry: isTrophyCaseEntry,
    searchInitiatedFrom: getSearchInitiatedFrom(state),
    currentPoint: String(getCurrentPoint(state)),
    entryPoint: state?.config?.entryPoint,
    currentPage: currentPage,
  };
}

function getPathDepth(currentPath: string): number {
  return currentPath ? currentPath.split('/').length - 1 : 0;
}

function isShallowPath(pathLen: number): boolean {
  return pathLen < 3;
}

function isEntryPoint(locationDetails: IStateLocationDetails): boolean {
  return (
    locationDetails.entryPoint &&
    parsePath(locationDetails.currentPoint) ===
      parsePath(locationDetails.entryPoint)
  );
}

function getReturnSearchPath(locationDetails: IStateLocationDetails): string {
  return locationDetails.searchInitiatedFrom === ''
    ? '/'
    : locationDetails.searchInitiatedFrom;
}

function getHasSearchedFromHome(
  locationDetails: IStateLocationDetails,
  pathExcludesPrograms: boolean,
): boolean {
  return (
    locationDetails.searchInitiatedFrom === '/' ||
    (locationDetails.searchInitiatedFrom === '' && pathExcludesPrograms)
  );
}

function isReturningToMyList(
  searchPathDepth: number,
  isSearchPage: boolean,
  currentPage: string,
): boolean {
  const myListPages = ['/favorites', '/continue-series', '/my-workouts'];
  return !searchPathDepth && !isSearchPage && myListPages.includes(currentPage);
}

function setHomeToolbarTitle(locationDetails: IStateLocationDetails): boolean {
  const pathLen = getPathDepth(locationDetails.currentPoint);
  const searchPathDepth = getPathDepth(locationDetails.searchInitiatedFrom);
  const shallowPath = isShallowPath(pathLen);
  const isSearchPage = locationDetails.currentPage === '/search';
  const returningToMyList = isReturningToMyList(
    searchPathDepth,
    isSearchPage,
    locationDetails.currentPage,
  );
  const pathExcludesPrograms = !locationDetails.currentPoint
    .split('/')
    .includes('programs');
  const hasSearchedFromHome = getHasSearchedFromHome(
    locationDetails,
    pathExcludesPrograms,
  );
  let rtnFlag = false;

  const titleCondition1: boolean =
    shallowPath &&
    !locationDetails.isChallengesEntry &&
    pathExcludesPrograms &&
    !isSearchPage;

  const titleCondition2: boolean =
    (shallowPath && hasSearchedFromHome) ||
    (searchPathDepth <= 1 &&
      locationDetails.searchInitiatedFrom === '/search' &&
      pathExcludesPrograms);

  if (titleCondition1 || titleCondition2) {
    rtnFlag = true;
  }

  if (returningToMyList) {
    rtnFlag = false;
  }

  return rtnFlag;
}

function handler(payload, store) {
  const state = store.getState();
  const locationDetails = extractStateLocationDetails(state);
  const onEntryPoint = isEntryPoint(locationDetails);
  const setHomeTitle = setHomeToolbarTitle(locationDetails);
  const basePath = parseBasePath(locationDetails.currentPoint);
  const returnFromSearchPath = getReturnSearchPath(locationDetails);
  const challengesPath = '/challenges';

  if (setHomeTitle) {
    setToolbarTitle(libraryPageTitle());
  }

  store.dispatch(createSearchInitiatedFrom(''));
  setItem(lsKey, false);

  if (basePath && basePath === 'trainer') {
    getSearchState().removeFilterByKey('trainerIds');
  }

  if (locationDetails.currentPage === '/search') {
    hideFilterMenu();
    if (!onEntryPoint) {
      // note: any searchable landing page will require manual nav here
      if (
        returnFromSearchPath.includes(challengesPath) ||
        returnFromSearchPath === '/'
      ) {
        navigateTo(returnFromSearchPath, store);
      } else {
        store.dispatch(goBack());
      }
      return;
    }
  }

  if (
    locationDetails.currentPage === challengesPath &&
    locationDetails.entryPoint === challengesPath &&
    locationDetails.isChallengesEntry &&
    locationDetails.isTrophyCaseEntry
  ) {
    store.dispatch(resetEntryPoint());
    nativeBridge.callNative({
      methodName: 'RunBehavior',
      key: 'NavigateToPath',
      path: 'trophy-case',
      respond: false,
    });
  } else if (onEntryPoint) {
    nativeBridge.callNative({
      methodName: 'NavigateBack',
    });
    store.dispatch(resetEntryPoint());
    navigateTo('/', store);
  } else if (locationDetails.currentPage !== '/') {
    hideFilterMenu();

    // go back
    store.dispatch(goBack());
    return;
  } else {
    // tell native to go back
    nativeBridge.callNative({
      methodName: 'NavigateBack',
    });
  }
}

export { handlerName, handler };
